/**
 * Composant Super Admin - Gestion des Bonus Crédits Programmés
 * Widget avec compte à rebours et planification
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Calendar, Plus, Trash2, Play, Search, Loader2, CheckCircle, AlertCircle, Coins, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../services/tokenService';
import ExportImportBar from './ExportImportBar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Widget Countdown individuel
const BonusCountdownWidget = ({ bonus, onCredit, onCancel }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const targetDate = new Date(bonus.scheduled_date).getTime();
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [bonus.scheduled_date]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className={`relative overflow-hidden ${isExpired ? 'border-amber-500 bg-amber-50' : 'border-emerald-200'}`}>
      {/* Effet gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5" />
      
      <CardContent className="relative pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg">
              <Gift className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">{bonus.user_name || bonus.user_email}</p>
              <p className="text-xs text-slate-500">{bonus.user_email}</p>
            </div>
          </div>
          <Badge className={`${
            bonus.status === 'pending' ? 'bg-amber-100 text-amber-700' :
            bonus.status === 'credited' ? 'bg-emerald-100 text-emerald-700' :
            'bg-red-100 text-red-700'
          }`}>
            {bonus.status === 'pending' ? 'En attente' :
             bonus.status === 'credited' ? 'Crédité' : 'Annulé'}
          </Badge>
        </div>

        {/* Montant du bonus */}
        <div className="flex items-center gap-2 mb-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
          <Coins className="h-6 w-6 text-amber-500" />
          <span className="text-2xl font-black text-emerald-700">+{bonus.credits}</span>
          <span className="text-sm text-emerald-600">crédits bonus</span>
        </div>

        {/* Message */}
        {bonus.message && (
          <p className="text-sm text-slate-600 mb-3 italic">"{bonus.message}"</p>
        )}

        {/* Countdown ou date */}
        {bonus.status === 'pending' && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
              <Calendar className="h-3 w-3" />
              <span>Programmé pour le {formatDate(bonus.scheduled_date)}</span>
            </div>

            {!isExpired ? (
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  { value: timeLeft.days, label: 'Jours' },
                  { value: timeLeft.hours, label: 'Heures' },
                  { value: timeLeft.minutes, label: 'Min' },
                  { value: timeLeft.seconds, label: 'Sec' }
                ].map((item, idx) => (
                  <div key={idx} className="text-center p-2 bg-slate-900 rounded-lg">
                    <p className="text-xl font-mono font-bold text-white">
                      {String(item.value).padStart(2, '0')}
                    </p>
                    <p className="text-[10px] text-slate-400 uppercase">{item.label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 bg-amber-100 rounded-lg mb-4 text-center">
                <p className="text-amber-700 font-semibold flex items-center justify-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Prêt à être crédité !
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={() => onCredit(bonus.id)}
                size="sm"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                <Play className="h-3 w-3 mr-1" />
                Créditer maintenant
              </Button>
              <Button
                onClick={() => onCancel(bonus.id)}
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </>
        )}

        {bonus.status === 'credited' && (
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">Crédité le {formatDate(bonus.credited_at)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function ScheduledBonusManager() {
  const [bonuses, setBonuses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);

  // Formulaire nouveau bonus
  const [newBonus, setNewBonus] = useState({
    user_id: '',
    credits: 20,
    scheduled_date: '',
    message: ''
  });

  // Charger les bonus
  const loadBonuses = useCallback(async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/credits/admin/scheduled-bonuses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBonuses(response.data.bonuses || []);
      setStats(response.data.stats || {});
    } catch {
      toast.error('Erreur lors du chargement des bonus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBonuses();
    const interval = setInterval(loadBonuses, 60000);
    return () => clearInterval(interval);
  }, [loadBonuses]);

  // Rechercher des utilisateurs
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setUsers([]);
      return;
    }
    
    setSearchingUsers(true);
    const token = getToken();
    try {
      const response = await axios.get(`${API}/credits/admin/users-for-bonus?search=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch {
    } finally {
      setSearchingUsers(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchUsers(searchUser);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchUser, searchUsers]);

  // Créer un bonus
  const handleCreateBonus = async () => {
    if (!newBonus.user_id || !newBonus.credits || !newBonus.scheduled_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setCreating(true);
    const token = getToken();
    try {
      await axios.post(`${API}/credits/admin/schedule-bonus`, newBonus, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bonus programmé avec succès !');
      setShowCreateModal(false);
      setNewBonus({ user_id: '', credits: 20, scheduled_date: '', message: '' });
      loadBonuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  // Créditer un bonus
  const handleCreditBonus = async (bonusId) => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/credits/admin/credit-bonus-now/${bonusId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.credits_added} crédits ajoutés !`);
      loadBonuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du crédit');
    }
  };

  // Annuler un bonus
  const handleCancelBonus = async (bonusId) => {
    if (!window.confirm('Voulez-vous vraiment annuler ce bonus ?')) return;
    
    const token = getToken();
    try {
      await axios.delete(`${API}/credits/admin/scheduled-bonus/${bonusId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bonus annulé');
      loadBonuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  // Filtrer les bonus en attente
  const pendingBonuses = bonuses.filter(b => b.status === 'pending');

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec stats */}
      <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <Gift className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Bonus Crédits Programmés</h2>
                <p className="text-white/80">Planifiez des récompenses pour vos utilisateurs</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-white text-amber-600 hover:bg-white/90 font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Programmer un bonus
            </Button>
          </div>
          <div className="mt-4">
            <ExportImportBar
              collection="scheduled_bonuses"
              label="Bonus programmés"
              onImported={() => window.location.reload()}
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="text-3xl font-black">{stats.pending || 0}</p>
              <p className="text-sm text-white/70">En attente</p>
            </div>
            <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="text-3xl font-black">{stats.credited || 0}</p>
              <p className="text-sm text-white/70">Crédités</p>
            </div>
            <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="text-3xl font-black">{stats.cancelled || 0}</p>
              <p className="text-sm text-white/70">Annulés</p>
            </div>
            <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="text-3xl font-black">{(stats.total_credits_pending || 0).toLocaleString()}</p>
              <p className="text-sm text-white/70">Crédits en attente</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des bonus en attente */}
      {pendingBonuses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pendingBonuses.map(bonus => (
            <BonusCountdownWidget
              key={bonus.id}
              bonus={bonus}
              onCredit={handleCreditBonus}
              onCancel={handleCancelBonus}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-slate-500">
            <Gift className="h-12 w-12 mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucun bonus programmé</p>
            <p className="text-sm">Créez votre premier bonus pour récompenser vos utilisateurs</p>
          </CardContent>
        </Card>
      )}

      {/* Modal création bonus */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg">
                <Gift className="h-5 w-5 text-white" />
              </div>
              Programmer un bonus
            </DialogTitle>
            <DialogDescription>
              Planifiez un bonus de crédits pour un utilisateur avec compte à rebours
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Recherche utilisateur */}
            <div className="space-y-2">
              <Label>Utilisateur *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par email ou nom..."
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                  className="pl-9"
                />
                {searchingUsers && (
                  <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-slate-400" />
                )}
              </div>
              {users.length > 0 && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {users.map(user => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setNewBonus(prev => ({ ...prev, user_id: user.id }));
                        setSearchUser(`${user.prenom || ''} ${user.nom || ''} (${user.email})`.trim());
                        setUsers([]);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between"
                    >
                      <span className="text-sm">{user.prenom} {user.nom}</span>
                      <span className="text-xs text-slate-500">{user.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Montant crédits */}
            <div className="space-y-2">
              <Label>Nombre de crédits *</Label>
              <div className="flex gap-2">
                {[10, 20, 50, 100, 200].map(amount => (
                  <Button
                    key={amount}
                    type="button"
                    variant={newBonus.credits === amount ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewBonus(prev => ({ ...prev, credits: amount }))}
                  >
                    {amount}
                  </Button>
                ))}
                <Input
                  type="number"
                  value={newBonus.credits}
                  onChange={(e) => setNewBonus(prev => ({ ...prev, credits: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                  min="1"
                />
              </div>
            </div>

            {/* Date programmée */}
            <div className="space-y-2">
              <Label>Date et heure de crédit *</Label>
              <Input
                type="datetime-local"
                value={newBonus.scheduled_date}
                onChange={(e) => setNewBonus(prev => ({ ...prev, scheduled_date: e.target.value }))}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label>Message (optionnel)</Label>
              <Input
                placeholder="Ex: Bonus fidélité décembre 2026"
                value={newBonus.message}
                onChange={(e) => setNewBonus(prev => ({ ...prev, message: e.target.value }))}
              />
            </div>

            {/* Prévisualisation */}
            {newBonus.user_id && newBonus.credits > 0 && newBonus.scheduled_date && (
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700 font-medium mb-2">Prévisualisation</p>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  <span className="font-bold text-emerald-800">+{newBonus.credits} crédits</span>
                  <span className="text-emerald-600">le {new Date(newBonus.scheduled_date).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateBonus}
              disabled={creating || !newBonus.user_id || !newBonus.credits || !newBonus.scheduled_date}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Gift className="h-4 w-4 mr-2" />
                  Programmer le bonus
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
